import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle, UploadSimple, Image, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sample images (visual examples only)
const SAMPLES = [
  {
    id: "jade-marble",
    name: "Jade Marble",
    image: "https://customer-assets.emergentagent.com/job_pure-epox-de/artifacts/a4d6eug5_42ee88d3-0562-4752-8458-3cfe93f51317.jpeg"
  },
  {
    id: "gold-metallic",
    name: "Gold Metallic",
    image: "https://customer-assets.emergentagent.com/job_pure-epox-de/artifacts/0p0q5hsb_f6b46605-6377-4472-8517-acdcd11d75a3.jpeg"
  },
  {
    id: "copper-lava",
    name: "Copper Lava",
    image: "https://customer-assets.emergentagent.com/job_pure-epox-de/artifacts/mqn1a6e7_fb2fb9f4-c087-48b8-8748-adeb8245f750.jpeg"
  },
  {
    id: "white-marble",
    name: "White Marble",
    image: "https://customer-assets.emergentagent.com/job_pure-epox-de/artifacts/r5ivd2z3_cad0a5ae-702e-4538-a959-fa0b2c575f7c.jpeg"
  }
];

const VIDEO_URL = "https://customer-assets.emergentagent.com/job_pure-epox-de/artifacts/0wap5wnq_6e6a15a1-fcac-4f62-94ca-6a1b51ad59d0.mp4";

const ProbeflaechePage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const formRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        toast.error("Bitte nur JPG, JPEG oder PNG Dateien hochladen.");
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Die Datei ist zu groß. Maximal 10MB erlaubt.");
        return;
      }
      setUploadedFile(file);
      setFilePreview(URL.createObjectURL(file));
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create form data for file upload
      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("email", formData.email);
      submitData.append("phone", formData.phone || "");
      submitData.append("message", formData.message || "");
      submitData.append("requestType", "Probefläche");
      if (uploadedFile) {
        submitData.append("image", uploadedFile);
      }

      const response = await axios.post(`${API}/sample-request-with-image`, submitData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success("Anfrage erfolgreich gesendet!");
        setFormData({ name: "", email: "", phone: "", message: "" });
        setUploadedFile(null);
        setFilePreview(null);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      // Fallback to regular contact endpoint without image
      try {
        const response = await axios.post(`${API}/contact`, {
          ...formData,
          message: `[Probeflächen-Anfrage]\n\n${formData.message}${uploadedFile ? '\n\n[Hinweis: Bild wurde angehängt, konnte aber nicht übertragen werden]' : ''}`
        });
        if (response.data.success) {
          setIsSubmitted(true);
          toast.success("Anfrage erfolgreich gesendet!");
          setFormData({ name: "", email: "", phone: "", message: "" });
          setUploadedFile(null);
          setFilePreview(null);
        }
      } catch (fallbackError) {
        toast.error("Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="probeflaeche-page">
      {/* Hero Section */}
      <section className="py-24 bg-slate-50" data-testid="probeflaeche-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900 mb-6">
              Probefläche auswählen
            </h1>
            <p className="font-body text-slate-600 text-lg leading-relaxed">
              Entdecken Sie unsere vielfältigen Epoxidharz-Designs und lassen Sie sich inspirieren.
            </p>
          </div>
        </div>
      </section>

      {/* Sample Gallery Section */}
      <section className="py-24 bg-white" data-testid="sample-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900 mb-4">
              Design-Beispiele
            </h2>
            <p className="font-body text-slate-600 max-w-2xl mx-auto">
              So können Epoxidharzflächen aussehen.
            </p>
          </div>

          {/* Images Grid - No Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {SAMPLES.map((sample) => (
              <div
                key={sample.id}
                className="relative rounded-lg overflow-hidden shadow-md ring-1 ring-slate-200"
                data-testid={`sample-card-${sample.id}`}
              >
                <div className="aspect-square">
                  <img 
                    src={sample.image} 
                    alt={sample.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="font-heading text-white text-lg font-semibold uppercase tracking-wide">
                    {sample.name}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Info Text */}
          <div className="bg-slate-50 rounded-lg p-6 mb-16 max-w-4xl mx-auto">
            <p className="font-body text-slate-600 text-center leading-relaxed">
              Die gezeigten Bilder dienen lediglich als Beispiele dafür, wie Epoxidharzflächen später aussehen können. 
              Jede Fläche wird individuell gestaltet und kann je nach Raum und Untergrund unterschiedlich wirken.
            </p>
          </div>

          {/* Sample Request Info Section */}
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight text-slate-900 mb-6">
              Möchten Sie eine Probefläche sehen?
            </h3>
            <p className="font-body text-slate-600 leading-relaxed mb-6">
              Gerne senden wir Ihnen Probeflächen zu, damit Sie sich selbst ein Bild von der Qualität 
              und Optik machen können. Nutzen Sie dafür einfach unser Kontaktformular und schreiben Sie 
              kurz, dass Sie eine Probefläche wünschen.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8">
              <p className="font-body text-amber-800 font-medium">
                Der Preis für Probeflächen erfolgt auf Anfrage.
              </p>
            </div>
            <Button 
              onClick={scrollToForm}
              className="btn-gold text-white rounded-sm font-bold uppercase tracking-wide px-8 py-6 h-auto text-base"
              data-testid="scroll-to-form-button"
            >
              Probefläche anfragen
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section ref={formRef} className="py-24 bg-slate-50" data-testid="contact-form-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900 mb-4">
              Anfrage senden
            </h2>
            <p className="font-body text-slate-600">
              Über dieses Formular können Sie Probeflächen anfragen oder ein Bild Ihrer Fläche hochladen.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-sm">
            {isSubmitted ? (
              <div className="bg-green-50 border border-green-200 rounded-sm p-8 text-center" data-testid="success-message">
                <CheckCircle size={48} weight="fill" className="text-green-600 mx-auto mb-4" />
                <h4 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  Vielen Dank!
                </h4>
                <p className="font-body text-slate-600">
                  Ihre Anfrage wurde erfolgreich versendet. Wir melden uns schnellstmöglich bei Ihnen.
                </p>
                <Button 
                  onClick={() => setIsSubmitted(false)}
                  className="mt-6 bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-semibold uppercase tracking-wide px-6 py-4 h-auto"
                  data-testid="new-request-button"
                >
                  Neue Anfrage
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="font-body font-medium text-slate-900">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-2 rounded-sm border-slate-300 focus:border-sky-700 focus:ring-sky-700"
                    placeholder="Ihr vollständiger Name"
                    data-testid="input-name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="font-body font-medium text-slate-900">
                    E-Mail *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-2 rounded-sm border-slate-300 focus:border-sky-700 focus:ring-sky-700"
                    placeholder="Ihre E-Mail-Adresse"
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="font-body font-medium text-slate-900">
                    Telefon (optional)
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-2 rounded-sm border-slate-300 focus:border-sky-700 focus:ring-sky-700"
                    placeholder="Ihre Telefonnummer"
                    data-testid="input-phone"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="font-body font-medium text-slate-900">
                    Nachricht *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="mt-2 rounded-sm border-slate-300 focus:border-sky-700 focus:ring-sky-700 resize-none"
                    placeholder="z.B. Ich möchte eine Probefläche anfragen..."
                    data-testid="input-message"
                  />
                </div>

                {/* File Upload */}
                <div>
                  <Label className="font-body font-medium text-slate-900">
                    Bild Ihrer Fläche hochladen (optional)
                  </Label>
                  <p className="font-body text-slate-500 text-sm mt-1 mb-3">
                    Sie können auch ein Foto Ihrer Fläche (z.B. Badezimmer, Wohnzimmer oder Garage) hochladen. 
                    Wir erstellen Ihnen auf Wunsch ein Beispielbild, wie eine Epoxidharzfläche bei Ihnen aussehen könnte.
                  </p>
                  
                  {!filePreview ? (
                    <div 
                      className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-sky-500 transition-colors cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                        onChange={handleFileChange}
                        className="hidden"
                        data-testid="file-input"
                      />
                      <UploadSimple size={32} className="mx-auto text-slate-400 mb-2" />
                      <p className="font-body text-slate-600 text-sm">
                        Klicken Sie hier, um ein Bild hochzuladen
                      </p>
                      <p className="font-body text-slate-400 text-xs mt-1">
                        JPG, JPEG oder PNG (max. 10MB)
                      </p>
                    </div>
                  ) : (
                    <div className="relative border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                          <img 
                            src={filePreview} 
                            alt="Vorschau" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-body text-slate-900 text-sm font-medium truncate">
                            {uploadedFile?.name}
                          </p>
                          <p className="font-body text-slate-500 text-xs">
                            {(uploadedFile?.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                          data-testid="remove-file-button"
                        >
                          <X size={20} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-gold text-white rounded-sm font-bold uppercase tracking-wide px-8 py-6 h-auto disabled:opacity-50"
                  data-testid="submit-button"
                >
                  {isSubmitting ? "Wird gesendet..." : "Anfrage absenden"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 bg-white" data-testid="video-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900 mb-4">
              So entsteht eine Epoxidharz Oberfläche
            </h2>
            <p className="font-body text-slate-600 max-w-2xl mx-auto">
              Erleben Sie den Entstehungsprozess eines Epoxidharz-Bodens in unserem Video.
            </p>
          </div>

          <div className="rounded-lg overflow-hidden shadow-xl">
            <video 
              controls
              className="w-full aspect-video bg-slate-900"
              data-testid="process-video"
            >
              <source src={VIDEO_URL} type="video/mp4" />
              Ihr Browser unterstützt das Video-Tag nicht.
            </video>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900" data-testid="probeflaeche-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mb-6">
            Noch Fragen?
          </h2>
          <p className="font-body text-slate-300 max-w-2xl mx-auto mb-8 text-lg">
            Kontaktieren Sie uns für eine persönliche Beratung. Wir helfen Ihnen gerne bei der Auswahl.
          </p>
          <a href="tel:+4916092653278">
            <Button 
              className="btn-gold text-white rounded-sm font-bold uppercase tracking-wide px-8 py-6 h-auto text-base"
              data-testid="cta-phone-button"
            >
              Jetzt anrufen
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ProbeflaechePage;
